/**
 * Workspace context change notification.
 *
 * The active workspace id is resolved lazily at app start (`ensureWorkspaceContext`
 * runs in a Layout effect, i.e. AFTER child route components have already
 * rendered) and can change later when the user switches workspace. Anything
 * caching workspace-scoped data therefore has to be told when the id arrives or
 * changes — otherwise a consumer that read `null` on first render keeps the
 * empty result forever.
 *
 * This module is intentionally dependency-free so both `tenantContext.ts` (the
 * authority) and `supabaseSync.ts` (the sessionStorage hint) can emit without
 * creating an import cycle with the caches that listen.
 */

export const WORKSPACE_CONTEXT_CHANGED_EVENT = 'workspace-context-changed';

/** Announce that the active workspace id was set, changed, or cleared. */
export function notifyWorkspaceContextChanged(workspaceId: string | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(WORKSPACE_CONTEXT_CHANGED_EVENT, { detail: { workspaceId } }),
  );
}

/** Subscribe to workspace context changes; returns an unsubscribe function. */
export function subscribeWorkspaceContext(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(WORKSPACE_CONTEXT_CHANGED_EVENT, listener);
  return () => window.removeEventListener(WORKSPACE_CONTEXT_CHANGED_EVENT, listener);
}
